import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, ControlContainer, FormGroup } from "@angular/forms";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { IOption } from "@tylertech/forge";

import { LgdFormControlFactory, LgdPatternValidator, setEnabled } from "@tylertech/lgd-reactive-forms";
import { isUserDefinedCheckboxField, isUserDefinedDateField, isUserDefinedMultiSelectField, isUserDefinedNumberField, isUserDefinedSelectField, isUserDefinedTextField, UserDefinedField } from "../../interfaces";
import { UserDefinedFieldViewModel } from "@tylertech/tyler-api-types";

@Component({
    selector: "user-defined-field",
    templateUrl: "./userDefinedField.tmpl.html",
    standalone: false
})
export class UserDefinedFieldComponent implements OnInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();
    private readonly optionsSubject = new BehaviorSubject<Array<IOption<string>>>([]);

    @Input() field: UserDefinedFieldViewModel;
    @Input() viewOnly: boolean;

    constructor(
        private readonly controlContainer: ControlContainer)
    { }

    private get controlName(): string { return `${this.field.ColumnName}Field`; }

    private get formGroup(): FormGroup
    {
        return this.controlContainer.control as FormGroup;
    }

    private get formControl(): AbstractControl
    {
        if (!this.formGroup || !this.controlName)
        {
            throw new Error("Cannot access either formGroup or controlName.");
        }

        return this.formGroup.get(this.controlName);
    }

    get ariaLabel(): string { return `Edit-${this.field.ColumnName}}-UserDefinedFields`; }

    get optionsObservable(): Observable<unknown[]> { return this.optionsSubject.asObservable(); }

    ngOnDestroy(): void
    {
        this.formGroup.removeControl(this.controlName);

        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        if (this.isCheckboxField(this.field))
        {
            const control = LgdFormControlFactory.checkbox(undefined, this.field.IsRequired, {});
            this.formGroup.addControl(this.controlName, control);

            control.setValue(this.parseBoolean(this.field.Value));
            this.subscribeToValueChanges(control, (newValue: boolean) =>
            {
                this.field.Value = newValue ? "True" : "False";
            });
        }
        else if (this.isDateField(this.field))
        {
            const control = LgdFormControlFactory.date(undefined, this.field.IsRequired, {});
            this.formGroup.addControl(this.controlName, control);

            control.setValue(this.field.Value
                ? new Date(this.field.Value)
                : undefined);
            this.subscribeToValueChanges(control, (newValue: Date) =>
            {
                if (!newValue || isNaN(newValue.getTime()))
                {
                    this.field.Value = null;
                }
                else
                {
                    const year = newValue.getFullYear();
                    const month = newValue.getMonth() + 1;
                    const day = newValue.getDate();
                    this.field.Value = month + "/" + day + "/" + year;
                }
            });
        }
        else if (this.isNumberField(this.field))
        {
            const control = LgdFormControlFactory.number(
                undefined,
                this.field.IsRequired,
                {
                    precision: this.field.Precision,
                    scale: this.field.Scale
                });
            this.formGroup.addControl(this.controlName, control);

            control.setValue(this.field.Value);
            this.subscribeToValueChanges(control, (newValue: number) =>
            {
                this.field.Value = newValue.toString();
            });
        }
        else if (this.isMultiSelectField(this.field))
        {
            console.warn(`Unsupported User Defined Field Data Type; was ${this.field.DataType}`);
        }
        else if (this.isSelectField(this.field))
        {
            this.optionsSubject.next(
                this.field.Options.map((x: string): IOption<string> =>
                {
                    return {
                        label: x,
                        value: x,
                    };
                }));

            const control = LgdFormControlFactory.select<IOption<string>>(
                undefined,
                this.field.IsRequired,
                {
                    displayValueFn: x => x.label
                });
            this.formGroup.addControl(this.controlName, control);

            control.setValue(this.optionsSubject.value.find(x => x.value === this.field.Value));
            this.subscribeToValueChanges(control, (newValue: IOption<string>) =>
            {
                this.field.Value = newValue.value;
            });
        }
        else if (this.isMaskedTextField(this.field))
        {
            const control = LgdFormControlFactory.text(
                undefined,
                this.field.IsRequired,
                {
                    maxLength: () => this.field.Length,
                },
                [
                    new UserDefinedFieldInputMaskValidator(this.field),
                ]);
            this.formGroup.addControl(this.controlName, control);

            control.setValue(this.field.Value);
            this.subscribeToValueChanges(control, (newValue: string) =>
            {
                this.field.Value = newValue.toString();
            });
        }
        else if (this.isTextField(this.field))
        {
            const control = LgdFormControlFactory.text(
                undefined,
                this.field.IsRequired,
                {
                    maxLength: () => this.field.Length,
                });

            this.formGroup.addControl(this.controlName, control);

            control.setValue(this.field.Value);
            this.subscribeToValueChanges(control, (newValue: string) =>
            {
                this.field.Value = newValue;
            });
        }
        else
        {
            throw new Error(`Unsupported User-Defined-Field DataType; was "${this.field.DataType}"`);
        }

        setEnabled(this.formControl, !this.viewOnly);
    }

    private subscribeTo<T = unknown>(observable: Observable<T>, subscriptionFn: (newValue: T) => void, destructor?: Subject<void>): void
    {
        observable.pipe(takeUntil(destructor || this.destructor$))
            .subscribe(x =>
            {
                subscriptionFn(x);
            });
    }

    private subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void, destructor?: Subject<void>): void
    {
        this.subscribeTo(control.valueChanges, subscriptionFn, destructor);
    }

    isTextField(field: UserDefinedFieldViewModel): field is UserDefinedField<"Text"> { return isUserDefinedTextField(field); }
    isUnMaskedTextField(field: UserDefinedFieldViewModel): field is UserDefinedField<"Text"> { return this.isTextField(field) && !field.InputMask; }
    isMaskedTextField(field: UserDefinedFieldViewModel): field is UserDefinedField<"Text"> { return this.isTextField(field) && !!field.InputMask; }
    isNumberField(field: UserDefinedFieldViewModel): field is UserDefinedField<"Number"> { return isUserDefinedNumberField(field); }
    isSelectField(field: UserDefinedFieldViewModel): field is UserDefinedField<"Select"> { return isUserDefinedSelectField(field); }
    isMultiSelectField(field: UserDefinedFieldViewModel): field is UserDefinedField<"MultiSelect"> { return isUserDefinedMultiSelectField(field); }
    isCheckboxField(field: UserDefinedFieldViewModel): field is UserDefinedField<"Check"> { return isUserDefinedCheckboxField(field); }
    isDateField(field: UserDefinedFieldViewModel): field is UserDefinedField<"Date"> { return isUserDefinedDateField(field); }

    private parseBoolean(value: string): boolean
    {
        return /^(1|true)$/i.test(value);
    }
}

class UserDefinedFieldInputMaskValidator extends LgdPatternValidator
{
    constructor(field: UserDefinedField<"Text">)
    {
        super(new RegExp(field.InputMask));
    }
}
